// Advisor data and dialogue system for SCD
// Two professors: Ouss Hervé (sarcastic, ex « Pr. Maurice Delorme » —
// renommé le 14/06/2026 sur demande utilisateur, id interne inchangé)
// and Camille Orsini (supportive)

export const ADVISOR_MOODS = {
  maurice: {
    satisfait: { label: 'Satisfait', image: '/advisors/maurice_satisfait.webp', quote: 'Évidemment.' },
    sceptique: { label: 'Sceptique', image: '/advisors/maurice_sceptique.webp', quote: 'Intéressant... mais non.' },
    serieux: { label: 'Sérieux', image: '/advisors/maurice_serieux.webp', quote: 'Écoutons les faits.' },
    sarcastique: { label: 'Sarcastique', image: '/advisors/maurice_sarcastique.webp', quote: 'Brillant. Vraiment brillant.' },
    impressionne: { label: 'Impressionné', image: '/advisors/maurice_impressionne.webp', quote: 'Passons à autre chose.' },
  },
  camille: {
    bienveillante: { label: 'Bienveillante', image: '/advisors/camille_bienveillante.webp', quote: 'Commençons par comprendre ensemble.' },
    franche: { label: 'Franche', image: '/advisors/camille_franche.webp', quote: "Ce n'est pas simple, et je ne vais pas le minimiser." },
    reflechie: { label: 'Réfléchie', image: '/advisors/camille_reflechie.webp', quote: "Voyons les options qui s'offrent à nous." },
    encourageante: { label: 'Encourageante', image: '/advisors/camille_encourageante.webp', quote: 'Vous êtes sur la bonne voie.' },
    fiere: { label: 'Fière', image: '/advisors/camille_fiere.webp', quote: "C'est un vrai progrès. Bravo." },
  },
};

export const ADVISORS = {
  maurice: {
    id: 'maurice',
    name: 'Ouss Hervé',
    shortName: 'M. Hervé',
    title: "Ancien directeur d'exploitation",
    age: 58,
    portrait: '/advisors/maurice_portrait.webp',
    description: "58 ans, ancien directeur d'exploitation chez un grand groupe de transport, reconverti enseignant après «en avoir assez de voir des gens faire n'importe quoi». Silhouette imposante, appétit légendaire, sarcasme affûté.",
    personality: "Il a toujours raison et le sait. Son sarcasme est une forme d'affection.",
    color: '#8B2500',
    accentColor: '#C41E3A',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-600',
    textColor: 'text-amber-900',
  },
  camille: {
    id: 'camille',
    name: 'Dr. Camille Orsini',
    shortName: 'Dr. Orsini',
    title: 'Docteure en sciences de gestion',
    age: 41,
    portrait: '/advisors/camille_portrait.webp',
    description: "41 ans, docteure en sciences de gestion, spécialiste du management de PME. Elle a grandi dans une famille de transporteurs.",
    personality: "Elle ne ment jamais sur la difficulté — elle accompagne vers la solution.",
    color: '#1F3864',
    accentColor: '#2563EB',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-900',
  },
};

// Event reactions: keyed by event category
export const EVENT_REACTIONS = {
  'Panne mécanique': {
    maurice: {
      mood: 'sceptique',
      dialogues: [
        "Encore une panne ? Quelle surprise. Avez-vous déjà entendu parler de la maintenance préventive, ou c'est trop moderne pour vous ?",
        "Un véhicule qui tombe en panne... probablement parce que personne ne le bichonne. Choisissez judicieusement.",
        "Bon. Ça arrive. Même aux meilleurs. Mais surtout à ceux qui négligent leur flotte.",
      ],
    },
    camille: {
      mood: 'franche',
      dialogues: [
        "Une panne, c'est frustrant. Voyons les options ensemble pour limiter l'impact sur vos opérations.",
        "Les pannes font partie du métier. L'important, c'est de réagir vite et de protéger vos contrats en cours.",
        "Ce n'est jamais agréable. Analysons les choix pour trouver le meilleur équilibre coût/délai.",
      ],
    },
  },
  'Météo': {
    maurice: {
      mood: 'serieux',
      dialogues: [
        "La météo ne se négocie pas. Au moins, ça, ce n'est pas de votre faute. Pour une fois.",
        "Écoutons les faits : la météo est mauvaise. La question, c'est ce que vous allez en faire.",
        "Le ciel se déchaîne. J'espère que votre planning est plus solide que vos excuses habituelles.",
      ],
    },
    camille: {
      mood: 'reflechie',
      dialogues: [
        "La météo est un facteur qu'on ne contrôle pas. Voyons comment adapter notre planning intelligemment.",
        "C'est une contrainte externe. Pesons le risque de continuer contre le coût du retard.",
        "Voyons les options qui s'offrent à nous. La sécurité de vos chauffeurs est prioritaire.",
      ],
    },
  },
  'Social / Grève': {
    maurice: {
      mood: 'sarcastique',
      dialogues: [
        "Une grève ! En France ! Quelle originalité. Bon, soyons pragmatiques...",
        "Ah, le dialogue social à la française. Brillant. Vraiment brillant. Que faites-vous ?",
        "Bienvenue dans le monde réel du transport. Les conflits, ça se gère, pas ça se subit.",
      ],
    },
    camille: {
      mood: 'bienveillante',
      dialogues: [
        "Les situations sociales sont délicates. Commençons par comprendre les enjeux avant de décider.",
        "C'est un moment où la communication compte autant que la décision financière.",
        "Prenons le temps d'évaluer chaque option. Il y a souvent une voie qui préserve les relations.",
      ],
    },
  },
  'Accident': {
    maurice: {
      mood: 'serieux',
      dialogues: [
        "Un accident. C'est sérieux. Pas le moment de faire le malin. Écoutons les faits et agissons.",
        "Là, je ne plaisante plus. La sécurité, c'est non négociable. Voyons les conséquences.",
        "Chaque accident est un rappel brutal. Gérez ça proprement, c'est tout ce qui compte maintenant.",
      ],
    },
    camille: {
      mood: 'franche',
      dialogues: [
        "C'est une situation grave. Je ne vais pas la minimiser. Voyons ensemble la meilleure marche à suivre.",
        "La priorité absolue, c'est la sécurité des personnes. Ensuite, on gère les conséquences.",
        "Ce n'est pas simple. Mais chaque choix ici a un impact à long terme sur votre réputation.",
      ],
    },
  },
  'Administratif': {
    maurice: {
      mood: 'sceptique',
      dialogues: [
        "L'administration... le cauchemar préféré des transporteurs. Voyons si vos papiers sont en ordre.",
        "Un contrôle ? Intéressant. J'espère que vous êtes du genre organisé. Non ? Dommage.",
        "La paperasse est l'ennemi invisible. Ceux qui la négligent le paient tôt ou tard.",
      ],
    },
    camille: {
      mood: 'reflechie',
      dialogues: [
        "Les obligations administratives font partie intégrante du métier. Voyons comment gérer cela au mieux.",
        "Chaque situation administrative est une opportunité de renforcer vos processus internes.",
        "Analysons calmement. Les décisions précipitées en matière administrative coûtent souvent plus cher.",
      ],
    },
  },
  'Marché / Économie': {
    maurice: {
      mood: 'serieux',
      dialogues: [
        "Le marché bouge. C'est là que se séparent les amateurs des professionnels. À vous de jouer.",
        "Les fluctuations économiques, c'est le quotidien. Celui qui s'adapte survit. Les autres... non.",
        "Écoutons les faits du marché. Votre réaction ici définira votre rentabilité des prochaines semaines.",
      ],
    },
    camille: {
      mood: 'reflechie',
      dialogues: [
        "Le contexte économique évolue. Voyons les options qui s'offrent à nous pour nous adapter.",
        "Chaque changement de marché porte des risques et des opportunités. Identifions-les ensemble.",
        "C'est le moment d'être stratégique. Ne réagissons pas dans l'urgence, mais avec lucidité.",
      ],
    },
  },
  'RH interne': {
    maurice: {
      mood: 'sceptique',
      dialogues: [
        "Problème RH... Ah, les joies du management humain. Essayez de ne pas trop vous planter.",
        "Un chauffeur mécontent, ça peut coûter plus cher qu'une panne. Réfléchissez avant de trancher.",
        "Les problèmes humains sont les plus complexes. Même moi, je ne prétends pas avoir toutes les réponses. Presque toutes.",
      ],
    },
    camille: {
      mood: 'bienveillante',
      dialogues: [
        "Les situations humaines sont toujours les plus délicates. Prenons le temps de bien comprendre.",
        "Vos chauffeurs sont votre plus grande richesse. Chaque décision ici impacte leur engagement.",
        "Commençons par comprendre la situation du point de vue de chacun. Puis décidons ensemble.",
      ],
    },
  },
  'Opportunité': {
    maurice: {
      mood: 'satisfait',
      dialogues: [
        "Tiens, une opportunité. Ne la gâchez pas. C'est le moment de montrer ce que vous valez.",
        "Voilà quelque chose d'intéressant. Pour une fois, c'est une bonne nouvelle. Saisissez-la. Ou pas.",
        "Évidemment, les opportunités arrivent quand on est préparé. Vous l'êtes... n'est-ce pas ?",
      ],
    },
    camille: {
      mood: 'encourageante',
      dialogues: [
        "Voilà une belle opportunité ! Vous êtes sur la bonne voie. Voyons comment en tirer le meilleur.",
        "C'est le genre de moment qui peut accélérer votre croissance. Analysons-la ensemble.",
        "Excellente nouvelle ! C'est votre travail acharné qui attire ce type d'opportunités.",
      ],
    },
  },
  'Véhicule occasion': {
    maurice: {
      mood: 'sceptique',
      dialogues: [
        "Un véhicule d'occasion... Vérifiez bien l'usure. Je ne compte plus les gens qui ont acheté des épaves déguisées.",
        "Intéressant. Le prix est attractif, mais n'oubliez pas : on ne fait pas d'affaires avec de la ferraille.",
        "L'occasion peut être une aubaine ou un gouffre. À vous de voir si vous savez évaluer un véhicule.",
      ],
    },
    camille: {
      mood: 'reflechie',
      dialogues: [
        "Une opportunité d'achat d'occasion. Évaluons ensemble le rapport qualité-prix avant de nous décider.",
        "Voyons les options. Un bon véhicule d'occasion peut être un excellent investissement à ce stade.",
        "C'est une décision importante. Regardons l'usure, le prix, et surtout si ça répond à un vrai besoin.",
      ],
    },
  },
};

// Situational reactions (non-event triggers)
export const SITUATIONAL_REACTIONS = {
  first_day: {
    maurice: {
      mood: 'serieux',
      dialogue: "Bien. Premier jour. Tout reste à faire. Recrutez un chauffeur, décrochez un contrat. Et ne perdez pas d'argent bêtement.",
    },
    camille: {
      mood: 'encourageante',
      dialogue: "Bienvenue dans l'aventure ! Commencez par recruter un chauffeur dans l'onglet RH, puis assignez-le à votre véhicule. Vous êtes prêt(e) !",
    },
  },
  treasury_low: {
    maurice: {
      mood: 'sarcastique',
      dialogue: "Votre trésorerie fond comme neige au soleil. Brillant. Il serait peut-être temps de générer des revenus, non ?",
    },
    camille: {
      mood: 'franche',
      dialogue: "Attention, votre trésorerie est basse. Ce n'est pas le moment de paniquer, mais de prioriser les contrats rentables.",
    },
  },
  treasury_critical: {
    maurice: {
      mood: 'serieux',
      dialogue: "Situation critique. Si vous ne réagissez pas immédiatement, votre entreprise n'existera plus demain.",
    },
    camille: {
      mood: 'franche',
      dialogue: "Votre trésorerie est en danger. Concentrons-nous uniquement sur la survie : contrats actifs, coûts réduits au minimum.",
    },
  },
  first_contract: {
    maurice: {
      mood: 'satisfait',
      dialogue: "Un premier contrat. C'est un début. Ne vous enflammez pas, il en faudra beaucoup d'autres. Évidemment.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Félicitations pour votre premier contrat ! C'est un vrai progrès. La machine est lancée !",
    },
  },
  good_profit: {
    maurice: {
      mood: 'impressionne',
      dialogue: "Hmm. Pas mal du tout aujourd'hui. Ne vous reposez pas sur vos lauriers, mais... pas mal. *se racle la gorge* Passons à autre chose.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Belle journée ! Vos efforts commencent à payer. Continuez sur cette lancée, vous êtes sur la bonne voie.",
    },
  },
  bad_result: {
    maurice: {
      mood: 'sceptique',
      dialogue: "Journée dans le rouge. Ça arrive. L'important, c'est que ce ne devienne pas une habitude. Sinon...",
    },
    camille: {
      mood: 'bienveillante',
      dialogue: "Ce n'était pas une bonne journée financièrement. Pas de panique, analysons ce qui peut être amélioré.",
    },
  },
  phase_up: {
    maurice: {
      mood: 'impressionne',
      dialogue: "Nouvelle phase débloquée. Je... *se racle la gorge* C'est correct. De nouveaux défis vous attendent. Ne baissez pas la garde.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Bravo ! Vous avez atteint un nouveau palier ! De nouvelles opportunités s'ouvrent. C'est un vrai progrès !",
    },
  },
  reputation_high: {
    maurice: {
      mood: 'satisfait',
      dialogue: "Votre réputation est excellente. Évidemment, avec un bon conseiller, ça aide. Mais je vous accorde le mérite.",
    },
    camille: {
      mood: 'encourageante',
      dialogue: "Votre réputation est remarquable ! C'est le fruit de vos décisions. Les meilleurs contrats viendront à vous.",
    },
  },
  reputation_low: {
    maurice: {
      mood: 'sarcastique',
      dialogue: "Votre réputation est au plus bas. Bravo, c'est presque un exploit. Redressez la barre avant qu'il ne soit trop tard.",
    },
    camille: {
      mood: 'franche',
      dialogue: "Votre réputation nécessite une attention urgente. Concentrons-nous sur la qualité de service pour remonter la pente.",
    },
  },
  milestone_30days: {
    maurice: {
      mood: 'satisfait',
      dialogue: "Un mois. Vous êtes encore là. C'est déjà mieux que la plupart. Évidemment, le plus dur commence maintenant.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Un mois d'activité ! C'est une belle étape. Prenez le temps d'analyser votre bilan mensuel pour progresser.",
    },
  },
  milestone_180days: {
    maurice: {
      mood: 'impressionne',
      dialogue: "Six mois. Je dois admettre... *se racle la gorge* ...vous avez du cran. Continuez.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Six mois ! Vous avez prouvé votre résilience. Votre entreprise a passé le cap critique. Bravo !",
    },
  },
  no_drivers: {
    maurice: {
      mood: 'sarcastique',
      dialogue: "Vous n'avez toujours aucun chauffeur. Vos véhicules ne vont pas se conduire tout seuls. Enfin... pas encore.",
    },
    camille: {
      mood: 'bienveillante',
      dialogue: "N'oubliez pas de recruter un chauffeur dans l'onglet RH pour pouvoir honorer vos contrats.",
    },
  },
  fleet_idle: {
    maurice: {
      mood: 'sceptique',
      dialogue: "Vos véhicules prennent la poussière au dépôt. Chaque jour sans contrat, c'est de l'argent jeté par les fenêtres.",
    },
    camille: {
      mood: 'reflechie',
      dialogue: "Plusieurs véhicules sont au repos. Pensez à décrocher de nouveaux contrats pour optimiser votre flotte.",
    },
  },
  achievement: {
    maurice: {
      mood: 'satisfait',
      dialogue: "Un succès débloqué. Bien. N'allez pas croire que ça fait de vous un expert. Mais c'est... encourageant.",
    },
    camille: {
      mood: 'fiere',
      dialogue: "Bravo pour ce succès ! Chaque étape franchie renforce votre entreprise. Continuez comme ça !",
    },
  },
  weekend: {
    maurice: {
      mood: 'serieux',
      dialogue: "Week-end. Pas de revenus, mais les charges courent toujours. Profitez-en pour planifier la semaine.",
    },
    camille: {
      mood: 'bienveillante',
      dialogue: "C'est le week-end. Vos chauffeurs récupèrent. Profitez-en pour revoir votre stratégie de la semaine.",
    },
  },
};

// Determine which mood image to show for a given advisor and mood key
import advisorsEN from './translations/advisors_en.json';
import { useLanguageStore } from '../i18n';

const isEN = () => useLanguageStore.getState().language === 'en';

export const getAdvisorMoodImage = (advisorId, moodKey) => {
  const moods = ADVISOR_MOODS[advisorId];
  if (!moods || !moods[moodKey]) {
    return ADVISORS[advisorId]?.portrait || '';
  }
  return moods[moodKey].image;
};

// Get a translated label for a mood (e.g. 'Pédagogue', 'Inquiet')
export const getAdvisorMoodLabel = (advisorId, moodKey) => {
  const moods = ADVISOR_MOODS[advisorId];
  if (!moods || !moods[moodKey]) return '';
  if (isEN()) {
    return advisorsEN.ADVISOR_MOODS?.[advisorId]?.[moodKey]?.label || moods[moodKey].label;
  }
  return moods[moodKey].label;
};

// Get a random dialogue for an event category
export const getEventDialogue = (advisorId, category) => {
  const reaction = EVENT_REACTIONS[category];
  if (!reaction || !reaction[advisorId]) return null;
  const { mood, dialogues } = reaction[advisorId];
  const idx = Math.floor(Math.random() * dialogues.length);
  let dialogue = dialogues[idx];
  if (isEN()) {
    const enDialogues = advisorsEN.EVENT_REACTIONS?.[category]?.[advisorId]?.dialogues;
    if (enDialogues && enDialogues[idx]) dialogue = enDialogues[idx];
  }
  return { mood, dialogue };
};

// Get a situational reaction
export const getSituationalReaction = (advisorId, situation) => {
  const reaction = SITUATIONAL_REACTIONS[situation];
  if (!reaction || !reaction[advisorId]) return null;
  if (isEN()) {
    const enDialogue = advisorsEN.SITUATIONAL_REACTIONS?.[situation]?.[advisorId]?.dialogue;
    if (enDialogue) {
      return { ...reaction[advisorId], dialogue: enDialogue };
    }
  }
  return reaction[advisorId];
};

// Get the localized advisor display fields
export const getLocalizedAdvisor = (advisorId) => {
  const a = ADVISORS[advisorId];
  if (!a) return null;
  if (!isEN()) return a;
  const en = advisorsEN.ADVISORS?.[advisorId];
  if (!en) return a;
  return { ...a, ...en };
};
